import type { Capabilities, Channel, WidgetManifest } from '@resto/contracts';
import { missingCapabilities } from '@resto/contracts';

export type SkipReason = 'channel' | 'capability' | 'host_version' | 'disabled' | 'delivery';

export interface Compatibility {
  ok: boolean;
  reason?: SkipReason;
  details?: string;
}

/** Сравнение semver: отрицательное — a старее b. Пре-релиз считается старее релиза. */
export function compareVersions(a: string, b: string): number {
  const parse = (value: string): { parts: number[]; prerelease: string | null } => {
    const [core = '', prerelease = null] = value.split('-', 2);
    const parts = core.split('.').map((part) => Number.parseInt(part, 10) || 0);
    return { parts, prerelease };
  };
  const left = parse(a);
  const right = parse(b);
  const length = Math.max(left.parts.length, right.parts.length);
  for (let index = 0; index < length; index += 1) {
    const diff = (left.parts[index] ?? 0) - (right.parts[index] ?? 0);
    if (diff !== 0) return diff;
  }
  if (left.prerelease === right.prerelease) return 0;
  if (left.prerelease === null) return 1;
  if (right.prerelease === null) return -1;
  return left.prerelease < right.prerelease ? -1 : 1;
}

export interface CompatibilityContext {
  channel: Channel;
  hostVersion: string;
  capabilities: Capabilities;
}

/**
 * Проверки перед загрузкой виджета. Хост обязан вызвать это ДО resolve:
 * виджет без нужных возможностей коннектора просто не показывается.
 */
export function checkHostCompatibility(
  manifest: WidgetManifest,
  context: CompatibilityContext,
): Compatibility {
  if (!manifest.channels.includes(context.channel)) {
    return {
      ok: false,
      reason: 'channel',
      details: `виджет не поддерживает канал ${context.channel}`,
    };
  }
  if (manifest.minHostVersion && compareVersions(context.hostVersion, manifest.minHostVersion) < 0) {
    return {
      ok: false,
      reason: 'host_version',
      details: `нужен хост ${manifest.minHostVersion}, у нас ${context.hostVersion}`,
    };
  }
  const missing = missingCapabilities(context.capabilities, manifest.capabilitiesRequired);
  if (missing.length > 0) {
    return { ok: false, reason: 'capability', details: `нет возможностей: ${missing.join(', ')}` };
  }
  return { ok: true };
}
